import { useEffect, useState } from "react";
import {
  CloseButton,
  StatusMessage,
  StyledInput,
} from "../../style/view.styles";
import {
  appUrl,
  fetchEntityData,
  getUserCredentials,
  saveEntityData,
} from "../../utils/RequestHandler";
import { getLoggedInUser } from "../../utils/AuthHandler";
import LoadingSpinner from "./LoadingSpinner";
import {
  FormContainer,
  StyledForm,
  StyledFormField,
  StyledLabel,
  StyledSelect,
  StyledTextarea,
  Subtitle,
  Title,
} from "../../style/modal.form.style";
import { FaClock, FaFile, FaPen } from "react-icons/fa";
import FullButton from "../Buttons/FullButton";

const ApplicationForm = ({
  selectedOpportunity,
  handleModalClose,
  setIsApplicationSubmitted,
}) => {
  const [availabilityOpt, setAvailabilityOpt] = useState([]);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);
  const [talentId, setTalentId] = useState(null);
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    cover_letter: "",
    resume: null,
    availability: "",
  });

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await fetchEntityData("availability_status");
        if (response.success) {
          setAvailabilityOpt(response.result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const setDefaultStatus = async () => {
      try {
        const response = await fetchEntityData("application_status");
        if (response.success) {
          setApplicationStatus(
            response.result.find((item) => item.status === "new").id
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    const setOrganization = async () => {
      try {
        const response = await fetchEntityData("organizations");
        if (response.success) {
          setOrganizationId(
            response.result.find(
              (item) => item.name === selectedOpportunity.organization
            ).id
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    const setTalent = async () => {
      try {
        const user = getLoggedInUser();
        const response = await fetchEntityData("talents", true);
        if (!response?.success || !Array.isArray(response.result)) {
          throw new Error("Invalid response format or request failed");
        }
        const data = response.result.find(
          (record) => record.email === user.username
        );
        setTalentId(data.id);
      } catch (error) {
        console.error("Error fetching talent profile:", error);
      } finally {
        setLoading(false);
      }
    };
    try {
      fetchAvailability();
      setDefaultStatus();
      setOrganization();
      setTalent();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedOpportunity.organization]);

  // Handle input changes for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file upload for resume
  const handleFileChange = async (e) => {
    const { files } = e.target;
    setUploading(true);
    setFormStatus({ message: "Uploading the resume...", type: "error" });
    try {
      const requestData = new FormData();
      requestData.append("file", files[0]);

      const headers = getUserCredentials();

      const response = await fetch(`${appUrl}/upload/`, {
        method: "POST",
        headers,
        body: requestData,
      });

      if (!response.ok) {
        setFormStatus({ message: "File upload failed", type: "error" });
        throw new Error("File upload failed");
      }
      setFormStatus({ message: "", type: "" });
      setUploading(true);
      const data = await response.json();
      setFormData((prev) => ({
        ...prev,
        resume: data.result,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  // Handle select box change for availability
  const handleSelectChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      availability: e.target.value,
    }));
  };

  const handleSubmitApplication = async () => {
    try {
      setSubmitting(true);
      const requestData = {
        ...formData,
        applicant: talentId,
        status: applicationStatus,
        opportunity: selectedOpportunity.id,
        organization: organizationId,
      };
      const response = await saveEntityData("job_applications", requestData);
      if (response.success) {
        setFormStatus({
          message: "Application sent successfully!",
          type: "success",
        });
        setIsApplicationSubmitted(true);
        handleModalClose();
      } else {
        setFormStatus({
          message:
            response.message ??
            "An error occurred while submitting your request. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setFormStatus({
        message:
          "An error occurred while submitting your application. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <FormContainer>
        <CloseButton onClick={handleModalClose}>X</CloseButton>
        <Title>Application Form</Title>
        <Subtitle>
          {selectedOpportunity?.title} at {selectedOpportunity?.organization}
        </Subtitle>
        <StyledForm>
          {loading && <LoadingSpinner />}
          {!loading && (
            <>
              <StyledFormField fullWidth>
                <StyledLabel>
                  <FaPen style={{ marginRight: 5 }} /> Cover letter
                </StyledLabel>
                <StyledTextarea
                  name="cover_letter"
                  placeholder="Cover letter"
                  rows="4"
                  value={formData.cover_letter}
                  onChange={handleInputChange}
                  required
                />
              </StyledFormField>
              <StyledFormField>
                <StyledLabel>
                  <FaFile style={{ marginRight: 5 }} /> Resume
                </StyledLabel>
                <StyledInput
                  type="file"
                  name="resume"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e)}
                  required
                />
              </StyledFormField>
              <StyledFormField>
                <StyledLabel>
                  <FaClock style={{ marginRight: 5 }} /> Availability
                </StyledLabel>
                <StyledSelect
                  name="availability"
                  value={formData.availability}
                  onChange={handleSelectChange}
                  required
                >
                  <option value="">Select availability</option>
                  {availabilityOpt.map((option) => (
                    <option key={option.status} value={option.id}>
                      {option.status}
                    </option>
                  ))}
                </StyledSelect>
              </StyledFormField>
            </>
          )}
        </StyledForm>
        <FullButton
          action={handleSubmitApplication}
          type="submit"
          disabled={submitting || uploading}
          title={submitting ? "Submitting..." : "Submit Application"}
        />
        {formStatus.message && (
          <StatusMessage type={formStatus.type}>
            {formStatus.message}
          </StatusMessage>
        )}
      </FormContainer>
    </>
  );
};

export default ApplicationForm;
